<script setup lang="ts">

import { computed } from "vue";
import { ConditionTile } from "../ConditionTile";
import { ConditionType } from "@domain/Vehicle";
import { isNullOrUndefined } from "is-what";
import type { ICondition } from "@domain/Vehicle/Condition/ICondition";
import type { IMyGarageViewModel } from "@app/MyGarage";

import CarbucksRewardsCondition from "./CarbucksRewardsCondition.vue";
import CarbucksProtectCondition from "./CarbucksProtectCondition.vue";

const model = defineModel<IMyGarageViewModel>( { required: true } );

const vehicle = model.value.vehicle;
const intervals = vehicle.condition.serviceSchedule.intervals;
const conditions = vehicle.condition.conditions;
const displayConditions = computed( () => conditions.filter( c => c.isEnabled && c.isVisible ) );
const disabledCount = computed( () => conditions.filter( c => c.isEnabled == false && c.isVisible ).length );

const onMarkDoneAsync = async ( condition: ICondition ) => 
{
    if( condition.type == ConditionType.Service )
    {
        var currentInterval = vehicle.condition.serviceSchedule.nextInterval;
        if( isNullOrUndefined( currentInterval ) ) return;

        // find the next interval that has higher kms than current interval, i only want one
        var upcomingInterval = intervals.find( i => i.km > currentInterval!.km );
        await model.value.onConditionSetServiceIntervalAsync( condition, upcomingInterval!, vehicle.condition.serviceSchedule );
    }

    else
    {
        await model.value.onConditionMarkDoneAsync( condition );
    }
};

</script>

<template>

    <div class="w-full bg-gray-900">

        <div class="flex flex-col">

            <div>

                <div class="h-6 bg-gray-850 flex items-center justify-center" />

                <!-- CONDITION ANCHOR BUTTONS -->
                <!-- <ConditionAnchors v-model="model" /> -->

                <!-- CARBUCKS REWARDS -->
                <template v-if="model.carePlan.carbucksRewards.isEnabled">
                    <CarbucksRewardsCondition v-model="model" />
                    <div class="h-10 bg-gray-850" />
                </template>

                <!-- CARBUCKS PROTECT -->
                <template v-if="model.carePlan.carbucksProtect.isEnabled">
                    <CarbucksProtectCondition v-model="model" />
                    <div class="h-10 bg-gray-850" />
                </template>

                <!-- <div class="bg-gray-850 px-4 2xl:px-6 pb-4">
                    <img 
                        src="@/assets/images/reminders.png"  
                        class="h-11 3xl:h-14" 
                    />
                    <div class="text-white/80 text-xs 2xl:text-sm">
                        Helpful reminders to keep your car in good shape
                    </div>
                </div> -->

           </div>

           <div class="
                flex flex-col px-3 2xl:px-6
                bg-gradient-to-b from-gray-850 to-gray-900 via-gray-850 via-80%
                ">

                <!-- ITERATE OVER CONDITIONS -->
                <ConditionTile 
                    v-for="(condition, index) in displayConditions"
                    :id="condition.isCritical || ConditionTile.isOverdue
                        ? 'critical' 
                        : condition.isDue ? 'due' 
                        : 'condition-' + index"
                    :key="condition.labels.name"
                    :update-key="condition.$updateKey"
                    v-model="displayConditions[ index ]"
                    @show-flag-tip="() => model.showFlagTip( condition )"
                    @mark-done="( condition ) => onMarkDoneAsync( condition )" 
                />

           </div>
            
           <RouterLink to="/my-garage/condition-settings"
                        class="flex flex-col items-center justify-center px-4 lg:px-6 py-4 mt-4 2xl:mt-6 w-full text-center"> 
                <Icon name="settings-levers" class="text-4xl 2xl:text-5xl mb-3" />
                <div v-if="disabledCount > 0" class="opacity-60">
                    {{ disabledCount }} Health Monitor{{ disabledCount != 1 ? 's are' : ' is' }} not shown<br />
                </div>
                <div v-else class="opacity-60">
                    The Health Monitors can be<br/>configured to suit your needs
                </div>
                <div class="mt-2">Click here to change your settings</div>
            </RouterLink>

        </div>

        <div class="bg-black/20">
            <div class="bg-gray-900 h-6
                    rounded-b-xl 2xl:rounded-b-3xl" 
            />        
        </div>

        <div class="min-h-[4rem] pt-4 px-4
                    bg-gradient-to-b from-black/20 to-gray-900"> 
        </div> 

    </div>

</template>